import { Alert, Button, Icon, Input } from "@showitinc/showit-wc";
import React, { useEffect, useState } from "react";

import "./Login.css";
import UserActions from "../../actions/UserActions";
import ShowitNameLogo from "../../components/ShowitNameLogo";
import AppConfig from "../../config/app";
import { useAccountState } from "../../hooks/query/useAccount";
import { resetCachesForLogin } from "../../utils/QueryUtils";

const validateEmail = (email) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

const LoginForm = () => {
  const user = useAccountState();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleInputChange = (evt) => {
    // Check if Enter key was pressed
    if (evt?.detail?.key === "Enter") {
      validateAndLogin();
    }
  };

  useEffect(resetCachesForLogin, []);

  useEffect(() => {
    // Setting the login view to always be "light" theme
    const originalClassList = document.body.className;
    document.body.classList.add("light");
    document.body.classList.remove("dark");

    // Cleanup function to restore the original class list on unmount
    return () => {
      document.body.className = originalClassList;
    };
  }, []);

  useEffect(() => {
    const loginError = user.get("loginError");

    if (loginError) {
      if (loginError.has("fields")) {
        loginError.get("fields").forEach((val) => {
          if (val.has("email")) {
            setEmailError(val.get("email"));
          }
          if (val.has("password")) {
            setPasswordError(val.get("password"));
          }
        });
      } else {
        const errorMessage = loginError.get("message");
        if (errorMessage && !errorMessage.toLowerCase().includes("invalid token")) {
          setPasswordError(loginError.get("message"));
        }
      }
    }
  }, [user]);

  const _login = (e) => {
    e.preventDefault();
    e.stopPropagation();
    validateAndLogin();
  };

  const validateAndLogin = () => {
    _resetErrors();

    const emailValid = validateEmail(email);
    const loginError = !emailValid || !password?.length;

    if (loginError) {
      if (!email.length) {
        setEmailError("Please enter your email address");
      } else if (!emailValid) {
        setEmailError("Please enter a valid email address");
      } else if (!password.length) {
        setPasswordError("Please enter your password");
      }
    } else {
      UserActions.login({ email, password });
    }
  };

  const _onCreateAccount = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `${AppConfig.createAccountURL}?email=${email}&_ref=app-login`;
  };

  const _onLostPassword = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = AppConfig.lostPasswordURL;
  };

  const _resetErrors = () => {
    setEmailError("");
    setPasswordError("");
  };

  return (
    <div className="login-view">
      <div
        style={{
          background: "transparent",
          zIndex: "100",
        }}
        className="form-login"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            _login(e);
          }
        }}
      >
        <h1 className="loginLogo" style={{ margin: "0 auto 0", width: "60%" }}>
          <ShowitNameLogo />
        </h1>

        <form id="showit-login">
          <Input
            className="login-input"
            name="username"
            hideLabel
            placeholder="Email"
            bordered
            error={!!emailError}
            onSIInput={(e) => setEmail(e.detail.value)}
            onSIKeydown={(e) => handleInputChange(e)}
            autoComplete="username"
            label="Email"
            type="email"
            size="large"
          >
            <Icon type="userLogin" slot="prefix" size="20" />
          </Input>

          {emailError && (
            <div className="error-wrap">
              <Alert
                data-e2e="ui-email-error-message"
                variant="error"
                weight="bold"
                message={emailError}
              />
            </div>
          )}

          <Input
            className="login-input"
            hideLabel
            placeholder="Password"
            bordered
            error={!!passwordError}
            type="password"
            name="current-password"
            autoComplete="current-password"
            onSIInput={(e) => setPassword(e.detail.value)}
            onSIKeydown={(e) => handleInputChange(e)}
            label="Password"
            size="large"
          >
            <Icon type="closedLock" slot="prefix" size="20" />
          </Input>

          {passwordError && (
            <div className="error-wrap">
              <Alert variant="error" weight="bold" message={passwordError} />
            </div>
          )}
          <div className="login-button-wrap">
            <Button
              type="submit"
              fullWidth
              form="showit-login"
              variant="feature"
              onClick={_login}
              data-e2e="ui-login-form-btn"
              size="lg"
            >
              Sign In
            </Button>
          </div>
          <div className="login-actions">
            <Button variant="minimal" lowercase onClick={_onCreateAccount}>
              Create an Account
            </Button>
            <Button variant="minimal" lowercase onClick={_onLostPassword}>
              Lost Password?
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
